import {Component, Material, Object3D, TextComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {CustomLogTag} from '../classes/Constants.js';
import {HUDModel} from '../models/HUDModel.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {FailStateNames} from '../enums/FailStateNames.js';
import {StatNames} from '../enums/StatNames.js';
import {Puzzle} from './puzzle.js';
import {UIModel} from '../models/UIModel.js';
import {Statistics} from './statistics.js';
import {AudioDirector} from './audio-director.js';
import {Sounds} from '../enums/Sounds.js';
import {formatNumber} from '../utils/Utils.js';
import {Localization} from '../utils/Localization.js';
import {formatString} from '../ui/utils/utils.js';
import {GameOverModel} from '../models/GameOverModel.js';
import {RNG} from '../utils/rng.js';
import {GameFlowController} from './game-flow-controller.js';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {TallyEndTotals} from './tally-end-totals.js';
import {FXPrefabs} from './fx-prefabs.js';
import {vec3} from 'gl-matrix';
import {WLUtils} from '../utils/WLUtils.js';
import {BoardColor, BoardObject} from './board-object.js';
import {UIText} from '../types/materials.js';
import {GameModeType} from '../enums/GameModeType.js';
import {EventEmitter} from '../utils/EventEmitter.js';
import {TextManager} from '../classes/text-manager.js';
import {UrgencyLevel} from '../enums/UrgencyLevel.js';

export enum ScoreType {
    Score,
    Coin,
    Time,
    Countdown,
    Boost,
}

export class ScoreController extends Component {
    static TypeName = 'score-controller';

    // Singleton
    private static _scoreInstance: ScoreController;
    private _urgency: UrgencyLevel;
    public get urgency(): UrgencyLevel {
        return this._urgency;
    }
    static get instance(): ScoreController {
        return ScoreController._scoreInstance;
    }
    private tempVec = vec3.create();
    // WLE Properties
    @property.float(0.05)
    scoreCountUpIncrement = 0.05;

    @property.object({required: true})
    columnWarning!: Object3D;

    @property.float(1.25)
    readySetGoHoldTime = 1.25;

    @property.string('')
    scoreAdded = '';

    @property.string('')
    coinScoreAdded = '';

    @property.string('')
    timeAdded = '';

    @property.string('')
    countdownAdded = '';

    @property.color(1.0, 0.5, 0.0, 1.0)
    orangeColor = [1.0, 0.5, 0.0, 1.0];

    @property.color(0.0, 1.0, 0.0, 1.0)
    greenColor = [0.0, 1.0, 0.0, 1.0];

    @property.color(0.0, 0.0, 1.0, 1.0)
    blueColor = [0.0, 0.0, 1.0, 1.0];

    @property.color(1.0, 1.0, 0.0, 1.0)
    yellowColor = [1.0, 1.0, 0.0, 1.0];

    @property.color(1.0, 1.0, 1.0, 1.0)
    whiteColor = [1.0, 1.0, 1.0, 1.0];

    @property.color(1.0, 0.0, 0.0, 1.0)
    redColor = [1.0, 0.0, 0.0, 1.0];

    @property.color(0.5, 0.0, 0.5, 1.0)
    purpleColor = [0.5, 0.0, 0.5, 1.0];

    @property.color(1.0, 0.75, 0.8, 1.0)
    pinkColor = [1.0, 0.75, 0.8, 1.0];

    @property.color(0.8, 0.8, 0.8, 1.0)
    moonColor = [0.8, 0.8, 0.8, 1.0];

    @property.color(0.6, 0.3, 0.0, 1.0)
    brownColor = [0.6, 0.3, 0.0, 1.0];

    @property.color(0.0, 1.0, 1.0, 1.0)
    turquoiseColor = [0.0, 1.0, 1.0, 1.0];

    @property.object({required: true})
    fruitSmashHammer: Object3D;

    @property.object({required: true})
    centerParent!: Object3D;

    @property.int(20)
    inGameChatterRandomness = 20;
    @property.float(2)
    inGameChatterTimeout = 2;

    // Properties
    private _level: number;
    get level() {
        return this._level;
    }
    set level(value) {
        const rules = Puzzle.instance.rules;
        this._level = value;
        let maxLevel = 25;

        if (rules != null) {
            maxLevel = rules.maxLevelToWin;

            if (maxLevel != 2) {
                // let format = Localization.instance.Get("LevelFormat");
                // string levelReached = level + "/" + maxLevel;
                // levelText.text = string.Format(format, levelReached);
                this.HUDModel.setLevel(`${this._level} / ${maxLevel}`);
            }

            if (this._level + 1 == maxLevel && maxLevel != 2) {
                //levelBarFlag.enabled = true;
            }
        }
    }

    private _coins: number = 0;
    get coins() {
        return this._cShadow != 0 ? this._cShadow ^ this._shadowMask : 0;
    }
    private _cShadow: number;
    private _shadowMask: number;

    private _combo: number = 0;
    get combo() {
        return this._combo;
    }

    private _HUDModel: HUDModel;
    get HUDModel(): HUDModel {
        return this._HUDModel;
    }

    private _score: number = 0;
    public get score(): number {
        return this._score;
    }

    private _gameIsOver: boolean;
    public get gameIsOver(): boolean {
        return this._gameIsOver;
    }

    private _isHighScore: boolean = false;
    public get isHighScore(): boolean {
        return this._isHighScore;
    }

    private _readySetGoOn = false;
    public get readySetGoOn() {
        return this._readySetGoOn;
    }

    private _gameOverNewRecord: string = '';
    public get gameOverNewRecord(): string {
        return this._gameOverNewRecord;
    }

    private _coroutineManager: CoroutineManager = new CoroutineManager();

    private _textColorMaterials: Map<BoardColor, Material> = new Map();

    private _raiseUrgencyDelegates: EventEmitter<ScoreController> =
        new EventEmitter<ScoreController>();
    private _lowerUrgencyDelegates: EventEmitter<ScoreController> =
        new EventEmitter<ScoreController>();

    private _oldArrowAngle: number = 0;
    private _newArrowAngle: number = 0;
    private _lastInGameChatter: number = -1;

    init() {
        if (ScoreController._scoreInstance) {
            this.engine.log.error(
                CustomLogTag.Game,
                'There can only be one instance of Puzzle Component'
            );
        }
        ScoreController._scoreInstance = this;
    }

    start() {
        this._HUDModel = new HUDModel();

        this.resetEasyMode();
        //ResetNormalMode();
        this.resetQuickplayMode();
        //ResetFungusMode();
        this.resetCupcakeMode();
        this.resetScore();
        this.resetTime();

        this.adjustLevelSlider(0);

        this.showEasyModeHud(false);
        // ShowNormalModeHud(false);
        // ShowTimeModeHud(false);
        // ShowSkullModeHud(false);
        this.showQuickplayModeHud(false);
        // ShowFungusModeHud(false);
        // ShowCupcakeModeHud(false);

        // Utils.SetActiveRecursively(fruitSmashHammer, false);

        // if (spawnedFreezeDropCap != null)
        // {
        // 	Destroy(spawnedFreezeDropCap);
        // }

        // timeTextColor = timeText.GetComponent<TweenColor>();
        // if (timeTextColor)
        // {
        // 	timeTextColor.enabled = false;
        // 	timeDefaultColor = timeTextColor.from;
        // }
        // timeClockScale.enabled = false;
        // timeClockColor.enabled = false;
        // timeClockColor.color = timeClockColor.from;
        // timeScaler.enabled = false;

        this.showColumnWarning(false);

        // textManager = TextManager.GetInstance();

        // lastInGameChatter = 0;

        // audioDirector = AudioDirector.GetDirector();

        // gameOverScreenReadyCallback = new GameFlowController.GameOverScreenReadyCallback(GameOverScreenReady);

        GameFlowController.instance.registerGameOverScreenReadyCallback(
            this._gameOverScreenReady
        );
    }

    addRaiseUrgencyDelegate(delegateFunc: (score: ScoreController) => void) {
        this._raiseUrgencyDelegates.add(delegateFunc);
    }
    removeRaiseUrgencyDelegate(delegateFunc: (score: ScoreController) => void) {
        this._raiseUrgencyDelegates.remove(delegateFunc);
    }
    addLowerUrgencyDelegate(delegateFunc: (score: ScoreController) => void) {
        this._lowerUrgencyDelegates.add(delegateFunc);
    }
    removeLowerUrgencyDelegate(delegateFunc: (score: ScoreController) => void) {
        this._lowerUrgencyDelegates.remove(delegateFunc);
    }

    update(dt: number) {
        this._coroutineManager.update(dt);
    }

    showShapeCount(_numShapeBonus: number) {}
    resetTime() {}
    resetScore() {
        this._score = 0;
        this._combo = 0;
        this.level = 1;

        // levelBarFlag.enabled = false;

        this.HUDModel.setSliderValue(0.001);
        // skullText.text = "";

        this.HUDModel.setScore(this._score);

        // ShowSkullModeHud(false);
        this.resetQuickplayMode();
        // ResetFungusMode();

        this.resetCupcakeMode();

        this.HUDModel.setReadySetGoText('');

        WLUtils.setActive(this.fruitSmashHammer, false);

        // if (spawnedFreezeDropCap != null)
        // {
        // 	Destroy(spawnedFreezeDropCap);
        // }

        // StopCoroutine("StartNewGameReadySetGo");
        this._coroutineManager.stopCoroutine(this._startNewGameReadySetGoId);
    }

    startNewGame() {
        this._coins = 0;
        this.HUDModel.setCoins(this._coins);
        this._shadowMask =
            RNG.getUniformInt(0, Number.MAX_SAFE_INTEGER / 2) + Number.MAX_SAFE_INTEGER / 2;
        this._cShadow = 0 ^ this._shadowMask;

        this._gameIsOver = false;
        this._isHighScore = false;

        // malInGameBubbleText.Clear();

        // Utils.SetActiveRecursively(fruitSmashHammer, false);

        // if (spawnedFreezeDropCap != null)
        // {
        // 	Destroy(spawnedFreezeDropCap);
        // }
        this.HUDModel.setShowGameOver(false);
        this.HUDModel.setShowMaxedLevel(false);

        if (Puzzle.instance.rules.showHUD()) {
            this._coroutineManager.stopCoroutine(this._startNewGameReadySetGoId);
            this._startNewGameReadySetGoId = this._coroutineManager.addCoroutine(
                new Coroutine(this._startNewGameReadySetGo())
            );
        }
    }

    addScore(newScore: number, isCombo: boolean) {
        this._score += newScore;

        this._coroutineManager.stopCoroutine(this._countUpScoreId);
        this._countUpScoreId = this._coroutineManager.addCoroutine(
            new Coroutine(this._countUpScore(newScore))
        );
        // Keep track of highest combo this game
        if (newScore > this._combo && isCombo == true) {
            this._combo = newScore;
        }

        const highCombo = Statistics.instance.getLong(StatNames.HighCombo);

        if (newScore > highCombo && isCombo == true) {
            Statistics.instance.record(StatNames.HighCombo, newScore);
        }
    }

    // TK: In Unity this was an overload of the above function, but in JS we can't do that. Since
    // this mostly does FX stuff I renamed it. Keep caution to call the correct method.
    addScoreFx(
        newScore: number,
        blob: Object3D,
        scoreType: ScoreType,
        numBlobs: number,
        canBeCombo: boolean
    ) {
        if (newScore > 0) {
            const targetCamera = this.engine.scene.activeViews[0].object;

            let defaultColor = BoardColor.Any; //TK:Was White, but there's no white in board colors.

            // bool alreadyLocal = false;

            let toSpawn: string;

            switch (scoreType) {
                case ScoreType.Score:
                    toSpawn = this.scoreAdded;
                    break;
                case ScoreType.Coin:
                    toSpawn = this.coinScoreAdded;
                    break;
                case ScoreType.Time:
                    toSpawn = this.timeAdded;
                    break;
                case ScoreType.Countdown:
                    toSpawn = this.countdownAdded;
                    break;
                case ScoreType.Boost:
                    // used w/ score multiplier boost
                    toSpawn = this.scoreAdded;
                    defaultColor = BoardColor.Red;
                    // alreadyLocal = true;
                    break;
                default:
                    toSpawn = this.scoreAdded;
                    break;
            }

            const scoreFX = FXPrefabs.instance.spawn(toSpawn, targetCamera);
            blob.getPositionWorld(this.tempVec);
            scoreFX.setPositionWorld(this.tempVec);
            const scoreFxText = scoreFX.getComponent(TextComponent);

            // GameObject scoreFX;

            // if (alreadyLocal == false)
            // {
            //     // instantiate score fx
            //     scoreFX = NGUITools.AddChild(scoreAddedParent, toSpawn);
            //     screenPos3D.z = 0;
            //     Vector3 screenPos = targetCamera.ScreenToWorldPoint(screenPos3D);

            //     scoreFX.transform.position = screenPos;
            // }
            // else
            // {
            //     scoreFX = NGUITools.AddChild(centerParent, toSpawn);
            //     screenPos3D.z = 0;
            //     scoreFX.transform.localPosition = scoreFX.transform.localPosition - new Vector3(0, 200.0f, 0);
            // }

            // UILabel scoreFXText = scoreFX.GetComponentInChildren<UILabel>();

            // get blob color from transform to color the text
            const blobObj = blob.getComponent(BoardObject);
            if (blobObj) {
                //     BoardObject.BoardColor myColor = blobObj.color;
                //     if (numBlobs >= numberInChainToSpawnDripFX)
                //     {
                //         SpawnDripFX(myColor);
                //     }
                scoreFxText.material = this.boardColorToMaterial(
                    blobObj.color,
                    scoreFxText.material
                );
            } else {
                scoreFxText.material = this.boardColorToMaterial(
                    defaultColor,
                    scoreFxText.material
                );
            }

            if (scoreFxText) {
                scoreFxText.text = '+' + formatNumber(newScore);
            }
            WLUtils.setActive(scoreFX, true);
            this.addScore(newScore, canBeCombo);
        }

        // should only do if not game over!
        // random chance to play chatter
        const rand = RNG.getUniformInt(0, this.inGameChatterRandomness);
        // don't do if in tutorial
        if (rand == 0 && Puzzle.instance.rules.showHUD() && this.gameIsOver == false) {
            this._chatterText();
        }
    }

    boardColorToMaterial(color: BoardColor, originalTextMaterial: Material): Material {
        let colorMaterial: Material = this._textColorMaterials.get(color);
        if (colorMaterial) {
            return colorMaterial;
        }
        let clonedMaterial = originalTextMaterial.clone() as UIText;
        switch (color) {
            case BoardColor.Orange:
                clonedMaterial.setColor(this.orangeColor);
                break;
            case BoardColor.Green:
                clonedMaterial.setColor(this.greenColor);
                break;
            case BoardColor.Blue:
                clonedMaterial.setColor(this.blueColor);
                break;
            case BoardColor.Yellow:
                clonedMaterial.setColor(this.yellowColor);
                break;
            case BoardColor.Red:
                clonedMaterial.setColor(this.redColor);
                break;
            case BoardColor.Purple:
                clonedMaterial.setColor(this.purpleColor);
                break;
            case BoardColor.Pink:
                clonedMaterial.setColor(this.pinkColor);
                break;
            case BoardColor.Moon:
                clonedMaterial.setColor(this.moonColor);
                break;
            case BoardColor.Brown:
                clonedMaterial.setColor(this.brownColor);
                break;
            case BoardColor.Turquoise:
                clonedMaterial.setColor(this.turquoiseColor);
                break;
            default:
                clonedMaterial.setColor(this.whiteColor);
                break;
        }
        this._textColorMaterials.set(color, clonedMaterial);
        return clonedMaterial;
    }

    showGameOverText() {
        this.HUDModel.setShowGameOver(true);
        // GameObject timeup = NGUITools.AddChild(centerParent, gameOverBigText);
        // timeup.transform.position = timeup.transform.position + gameOverBigTextOffset;
        // AspectRatioScale aspect = timeup.GetComponentInChildren<AspectRatioScale>();
        // aspect.CalculateScale();
        // UILabel timeupText = timeup.GetComponentInChildren<UILabel>();
        // timeupText.text = Localization.instance.Get("GameOver");
        // ZoomOut zoom = timeup.GetComponentInChildren<ZoomOut>();
        // zoom.timerStart = 2.0f;
    }

    showMaxedLevelText() {
        this.HUDModel.setShowMaxedLevel(true);
        //console.log('Maxed Level');
        // GameObject timeup = NGUITools.AddChild(centerParent, gameOverBigText);
        // UILabel timeupText = timeup.GetComponentInChildren<UILabel>();
        // AspectRatioScale aspect = timeup.GetComponentInChildren<AspectRatioScale>();
        // aspect.CalculateScale();
        // timeupText.text = Localization.instance.Get("ReachedMaxLevelWin");
        // timeupText.color = new Color32(255,255,100,255);
        // //TweenScale levscale = timeup.GetComponentInChildren<TweenScale>();
        // //levscale.to = new Vector3(1.1f,1.10f,1f);
        // ZoomOut zoom = timeup.GetComponentInChildren<ZoomOut>();
        // zoom.timerStart = 2.0f;
    }

    gameOver(failState: FailStateNames) {
        // set bool so that no more random chatter will play
        this._gameIsOver = true;

        // turn off warning
        this.showColumnWarning(false);

        // turn off sfx off warning
        this.showSkullWarning(false);

        // GameObject levelUpFX = NGUITools.AddChild(levelUpParent, levelUpText);
        // levelUpFX.transform.position = levelUpFX.transform.position - levelUpBalloonOffset;;

        // UILabel levelUpFXText = levelUpFX.GetComponentInChildren<UILabel>();

        // 1 out of 3 will be generic game over text
        // let key = '';
        // if (failState == FailStateNames.TutorialWon) {
        //     key = 'TutorialPassed';
        // } else if (failState == FailStateNames.MaxLevelReached) {
        //     key = textManager.GetGameOverWinText();
        // } else {
        //     let rand = RNG.getUniformInt(0, 3);
        //     if (rand == 0) {
        //         switch (failState) {
        //             case FailStateNames.BlobsOverflow:
        //                 key = textManager.GetGameOverBlobText();
        //                 break;

        //             case FailStateNames.TimeUp:
        //                 key = textManager.GetGameOverTimedText();
        //                 break;

        //             case FailStateNames.CountdownExpired:
        //                 key = textManager.GetGameOverSkullText();
        //                 break;

        //             case FailStateNames.FruitOverflow:
        //                 key = textManager.GetGameOverFruitText();
        //                 break;

        //             default:
        //                 key = textManager.GetGameOverGenericText();
        //                 break;
        //         }
        //     } else {
        //         key = textManager.GetGameOverGenericText();
        //     }
        // }

        // this.levelUpFXText = Localization.instance.get(key);

        // // Remove any text that is currently up
        // if (malInGameBubbleText.Count > 0)
        // {
        //     foreach (GameObject guy in malInGameBubbleText)
        //     {
        //         Destroy(guy);
        //     }
        //     malInGameBubbleText.Clear();
        // }
        // // Remove LEVEL UP text
        // if (levelUpCenterText.Count > 0)
        // {
        //     foreach (GameObject guy in levelUpCenterText)
        //     {
        //         Destroy(guy);
        //     }
        //     levelUpCenterText.Clear();
        // }
    }

    showColumnWarning(visible: boolean) {
        this.columnWarning.active = visible;

        const newUrgency = visible ? UrgencyLevel.High : UrgencyLevel.Low;
        if (this._urgency !== newUrgency) {
            this._urgency = newUrgency;
            if (visible) {
                this._raiseUrgencyDelegates.emit(this);
                this._chatterText(true);
            } else {
                this._lowerUrgencyDelegates.emit(this);
            }
        }
    }

    showSkullWarning(turnon: boolean) {
        // Utils.SetActiveRecursively(skullModeWarning, turnon);
        // Utils.SetActiveRecursively(skullModeWarning2, turnon);
    }

    setQuickplayText(left, total) {
        const fruitHUD = Localization.instance.get('FruitHUD');
        this.HUDModel.setLevel(formatString(fruitHUD, left));
    }

    showHUD() {
        UIModel.instance.setHUDActive(true);
    }

    hideHUDImmediately() {
        UIModel.instance.setHUDActive(false);
        // if (puzzle.Rules.ShowHUD())
        //     {
        //         if (HUDAnimation.Length > 0)
        //         {
        //             for (int i = 0; i < HUDAnimation.Length; i++)
        //             {
        //                 ActiveAnimation.Play(HUDAnimation[i], HUDUpClip.name, AnimationOrTween.Direction.Forward, AnimationOrTween.EnableCondition.EnableThenPlay, AnimationOrTween.DisableCondition.DoNotDisable);
        //                 HUDAnimation[i].GetComponent<Animation>()["HUDmoveUp"].normalizedTime = 1.0f;
        //             }
        //         }
        //     }

        //     // turn off ready set go if on
        //     if (readySetGoOn == true)
        //     {
        //         Utils.SetActiveRecursively(readySetGoText, false);
        //     }
    }

    //TK: In the original the hud stays active, but just animates out of the way.
    //    We don't have that need to hide it.
    hideHUD() {
        // UIModel.instance.setHUDActive(false);
        // if (puzzle.Rules.ShowHUD())
        // {
        // 	if (HUDAnimation.Length > 0)
        // 	{
        // 		for (int i = 0; i < HUDAnimation.Length; i++)
        // 		{
        // 			ActiveAnimation.Play(HUDAnimation[i], HUDUpClip.name, AnimationOrTween.Direction.Forward, AnimationOrTween.EnableCondition.EnableThenPlay, AnimationOrTween.DisableCondition.DoNotDisable);
        // 		}
        // 	}
        // }
    }

    showFinalResults(
        description: string,
        finalValue: number,
        gameMode: string,
        leaderboardStat: StatNames
    ) {
        //gameOverLeaderboard.LeaderboardStat = leaderboardStat;
        //gameOverModeLabel.text = gameMode;
    }

    adjustLevelSlider(percentage: number) {
        const currentSlider = this.HUDModel.sliderValue;

        if (percentage != 0 || currentSlider == 1) {
            this._coroutineManager.stopCoroutine(this._adjustLevelSliderDynamicallyId);
            this._adjustLevelSliderDynamicallyId = this._coroutineManager.addCoroutine(
                new Coroutine(this._adjustLevelSliderDynamically(percentage, currentSlider))
            );
        } else {
            this.HUDModel.setSliderValue(0.001);
        }
    }

    setLevelSlider(percentage: number) {
        const currentSlider = this.HUDModel.sliderValue;

        if (percentage != 0 || currentSlider == 1) {
            this.HUDModel.setSliderValue(percentage);
        } else {
            this.HUDModel.setSliderValue(0.001);
        }
    }

    addCoins(newCoins: number) {
        this._coins += newCoins;
        //coinText.text = Utils.FormatNumber(coins);
        this.HUDModel.setCoins(this._coins);

        this._cShadow = ((this._cShadow ^ this._shadowMask) + newCoins) ^ this._shadowMask;

        //coinScaler.Reset();
        //coinScaler.Play(true);
    }

    setupGameOverResults(
        highScore: boolean,
        weeklyHigh: boolean,
        initialRank: number,
        initialXp: number,
        newXp: number
    ) {
        GameOverModel.instance.setScore(this._score);
        if (highScore) {
            this._isHighScore = true;
            this._gameOverNewRecord = Localization.instance.get('NewRecord');
        } else if (weeklyHigh) {
            this._isHighScore = true;
            this._gameOverNewRecord = Localization.instance.get('WeeklyRecord');
        } else {
            this._gameOverNewRecord = '';
        }
        GameOverModel.instance.setHighscoreText(this._gameOverNewRecord);

        const tallyIt = this.object.getComponent(TallyEndTotals);
        tallyIt.setUp(this._cShadow ^ this._shadowMask, initialRank, initialXp, newXp);

        // this.gameoverModel.setCoins('+' + formatNumber(this._cShadow ^ this._shadowMask));
        // const totalCoins = Statistics.instance.getTransactional(
        //     TransactionalNames.CoinCount
        // );
        // this.gameoverModel.setTotalCoins(formatNumber(totalCoins));
        // this.gameoverModel.setRank(initialRank.toString());

        this._coins = 0;
        this._cShadow = 0;
    }

    remainingFruitBigText(text: string) {
        this._HUDModel.setLastWaveText(text);
        // // spawn large level up text
        // GameObject last = NGUITools.AddChild(centerParent, lastWaveText);
        // UILabel goText = last.GetComponentInChildren<UILabel>();

        // AspectRatioScale aspect = last.GetComponentInChildren<AspectRatioScale>();
        // aspect.CalculateScale();

        // goText.color = new Color32(255,200,225,255);
        // goText.text = text;

        // TweenColor tween = last.GetComponentInChildren<TweenColor>();
        // tween.from = new Color32(255,200,225,225);
        // tween.to = new Color32(255,200,225,0);

        // levelUpCenterText.Add(last);
    }

    resetQuickplayMode() {
        this.showQuickplayModeHud(false);
    }

    resetEasyMode() {
        this.showEasyModeHud(false);
    }

    resetCupcakeMode() {
        this.showCupcakeModeHud(false);
    }

    showQuickplayModeHud(turnon: boolean) {
        //TK TODO: Add the quickplay mode hud (I think it's the stars)
        //Utils.SetActiveRecursively(quickplayModeHud, turnon);
        if (turnon) {
            this._HUDModel.setGameMode(GameModeType.Quickplay);
        }
        this.showStarNotice(turnon);
    }

    showEasyModeHud(turnon: boolean) {
        if (turnon) {
            this._HUDModel.setGameMode(GameModeType.Easy);
        }
        // easyModeText.text = " ";
    }

    showCupcakeModeHud(turnon: boolean) {
        if (turnon) {
            this._HUDModel.setGameMode(GameModeType.Cupcake);
        }
    }

    showStarNotice(turnon: boolean) {
        // if (turnon == true)
        // {
        // 	if (!starNotice.activeInHierarchy)
        // 	{
        // 		Utils.SetActiveRecursively(starNotice, true);
        // 		TweenAlpha[] alphSet = starNotice.GetComponentsInChildren<TweenAlpha>();
        // 		for (int x = 0; x < alphSet.Length; x++)
        // 		{
        // 			alphSet[x].Reset();
        // 		}
        // 	}
        // 	if (!starNotice2.activeInHierarchy)
        // 	{
        // 		Utils.SetActiveRecursively(starNotice2, true);
        // 		TweenAlpha[] alphSet2 = starNotice2.GetComponentsInChildren<TweenAlpha>();
        // 		for (int x = 0; x < alphSet2.Length; x++)
        // 		{
        // 			alphSet2[x].Reset();
        // 		}
        // 	}
        // }
        // else
        // {
        // 	Utils.SetActiveRecursively(starNotice, false);
        // 	Utils.SetActiveRecursively(starNotice2, false);
        // }
    }

    setTachometerArrow(newAngle: number) {
        this._newArrowAngle = newAngle;
        this._HUDModel.setTachometerValue(newAngle);
    }

    changeTachometerArrow(newAngle: number) {
        this._oldArrowAngle = this._newArrowAngle;
        this._newArrowAngle = newAngle;
        if (this._newArrowAngle != this._oldArrowAngle) {
            this._coroutineManager.stopCoroutine(this._tachoMeterInterpolateId);
            this._tachoMeterInterpolateId = this._coroutineManager.addCoroutine(
                new Coroutine(
                    this.tachometerInterpolate(this._oldArrowAngle, this._newArrowAngle)
                )
            );
        }
    }

    addStarCount(total: number) {
        this._HUDModel.setStarCounter(total);

        //quickplayHudScale.Reset();
        //quickplayHudScale.Play(true);
    }

    addCupcakesCount(total: number) {
        this._HUDModel.setCupcakeCounter(total);
        //cupcakeHudScale.Reset();
        //cupcakeHudScale.Play(true);
    }

    private _tachoMeterInterpolateId: number = -1;
    private *tachometerInterpolate(oldRotation: number, rotation: number) {
        let transitionTime = 30;
        let increment;
        let difference;
        let tempRotation = 0;
        if (oldRotation > rotation) {
            difference = oldRotation - rotation;
            increment = difference / transitionTime;
            for (let i = 0; i < difference; i += increment) {
                tempRotation = oldRotation - i;
                this.HUDModel.setTachometerValue(tempRotation);
                yield* waitForSeconds(0.01);
            }
        } else {
            difference = rotation - oldRotation;
            increment = difference / transitionTime;
            for (let i = 0; i < difference; i += increment) {
                tempRotation = oldRotation + i;
                this.HUDModel.setTachometerValue(tempRotation);
                yield* waitForSeconds(0.01);
            }
        }
        this.HUDModel.setTachometerValue(tempRotation);
    }

    private _startNewGameReadySetGoId: number = -1;
    private *_startNewGameReadySetGo() {
        while (!Puzzle.instance.gameInProgress) {
            yield null;
        }

        //pause so that tutorial popup can show and wont show over 'Ready'
        yield* waitForSeconds(0.75);

        this.showHUD();
        yield null; // wait for the next frame

        this._readySetGoOn = true;

        // TweenScale zoomOut = readySetGoText.GetComponentInChildren<TweenScale>();
        // if (zoomOut)
        // {
        // 	zoomOut.Play(true);
        // }

        // stupid thing has a shadow that is another field. need to find all labels
        //UILabel goText = readySetGoText.GetComponentInChildren<UILabel>();

        this._HUDModel.setReadySetGoText(Localization.instance.get('Ready'));

        // SFX
        AudioDirector.instance.playOneShot(Sounds.readySFX, true);

        yield* waitForSeconds(this.readySetGoHoldTime);

        AudioDirector.instance.playOneShot(Sounds.readySFX, true);

        this._HUDModel.setReadySetGoText(Localization.instance.get('Set'));

        yield* waitForSeconds(this.readySetGoHoldTime);

        AudioDirector.instance.playOneShot(Sounds.goSFX, true);

        this._HUDModel.setReadySetGoText(Localization.instance.get('Go'));

        yield* waitForSeconds(this.readySetGoHoldTime);

        // if (zoomOut)
        // {
        // 	zoomOut.Play(false);
        // 	yield return new WaitForSeconds(zoomOut.duration);
        // }
        this._readySetGoOn = false;

        this._HUDModel.setReadySetGoText('');
        //Utils.SetActiveRecursively(readySetGoText, false);
    }

    private _adjustLevelSliderDynamicallyId: number = -1;
    private *_adjustLevelSliderDynamically(newSlider: number, oldSlider: number) {
        const transitionTime = 0.4;

        if (newSlider > 1) {
            newSlider = 1;
        }

        if (oldSlider > newSlider) {
            oldSlider = 0.001;
        }

        const increment = (newSlider - oldSlider) / transitionTime;

        for (let i = 0; i < newSlider - oldSlider; i += increment / 60.0) {
            this.HUDModel.setSliderValue(oldSlider + i);
            yield* waitForSeconds(0.01);
        }

        //  when it's full, reset to 0
        if (this.HUDModel.sliderValue == 1) {
            this.HUDModel.setSliderValue(0.001);
        }
    }

    private _countUpScoreId: number = -1;
    private *_countUpScore(newScore: number) {
        let finalScore = this._score;
        let tempScore = this._score - newScore;

        let increment = Math.ceil(newScore * this.scoreCountUpIncrement);

        for (let x = tempScore; x <= finalScore; x += increment) {
            tempScore = x;
            this._HUDModel.setScore(tempScore);
            yield* waitForSeconds(0.01); // Wait for 0.01 seconds.
        }
        this._HUDModel.setScore(finalScore);
    }

    private _gameOverScreenReady = () => {
        this.HUDModel.setShowGameOver(false);
        this.HUDModel.setShowMaxedLevel(false);
        if (this._isHighScore == true) {
            AudioDirector.instance.playOneShot(Sounds.newHighScoreSFX, true);
        }
    };

    private _chatterText(force: boolean = false) {
        if (this.HUDModel.malSpeech != '' && !force) {
            // already showing text, so don't show again
            return;
        }
        const possibleTexts =
            this._urgency == UrgencyLevel.Low
                ? TextManager.instance.inGameChatter
                : TextManager.instance.inGameChatterRisingColumn;

        // GRAB RANDOM TEXT
        let rand = RNG.getUniformInt(0, possibleTexts.length - 1);
        // TK: Technically it might compare the ingameChatter text with the inGameChatterRisingColumn text
        //     resolving that adds a lot of code and it doesn't really matter
        if (rand == this._lastInGameChatter) {
            rand = (rand + 1) % possibleTexts.length;
        }
        this._lastInGameChatter = rand;
        const text = Localization.instance.get(possibleTexts[rand]);
        this.HUDModel.setMalSpeech(text);

        this._coroutineManager.stopCoroutine(this._textTimeoutId);
        this._textTimeoutId = this._coroutineManager.addCoroutine(
            new Coroutine(this._textTimeout())
        );
    }

    private _textTimeoutId: number = -1;
    private *_textTimeout() {
        yield* waitForSeconds(this.inGameChatterTimeout);
        this.HUDModel.setMalSpeech('');
    }
}
